import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiService, Match } from '../services/api';

const Schedule: React.FC = () => {
  const { year, group } = useParams<{ year: string; group: string }>();
  const [matches, setMatches] = useState<Match[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

  console.log('Schedule component rendered with params:', { year, group });

  // Helper function to construct logo URL from team ID
  const getTeamLogoUrl = (teamId: string): string => {
    return `https://pub-e0ccb659989a49a297dc22911f822b08.r2.dev/photos/teams/logos/${teamId}.png`;
  };

  // State to track which logos failed to load
  const [failedLogos, setFailedLogos] = useState<Set<string>>(new Set());

  // Handle logo load error
  const handleLogoError = (teamId: string) => {
    setFailedLogos(prev => new Set(Array.from(prev).concat(teamId)));
  };

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if logo failed to load
  const logoFailed = (teamId: string): boolean => {
    return failedLogos.has(teamId);
  };

  useEffect(() => {
    const fetchData = async () => {
      console.log('Schedule: fetchData called with:', { year, group });
      
      if (!year || !group) {
        console.log('Schedule: Missing year or group parameters');
        setError('Invalid URL parameters');
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        console.log('Schedule: Fetching matches...');
        const matchesData = await apiService.getMatches();
        console.log('Schedule: Received matches data:', matchesData?.length || 0, 'matches');
        
        if (!Array.isArray(matchesData)) {
          console.error('Schedule: Invalid matches data format:', matchesData);
          throw new Error('Invalid data format received from API');
        }
        
        // Filter matches for the specific year and group - include ALL matches
        const yearNum = parseInt(year);
        const filteredMatches = matchesData.filter(
          match => {
            const matchYearMatches = match.year === yearNum;
            const matchGroupMatches = match.group === group;
            console.log(`Schedule: Match ${match.id} - year: ${match.year} (${matchYearMatches}), group: ${match.group} (${matchGroupMatches}), teams: ${match.team1?.name || 'null'} vs ${match.team2?.name || 'null'}`);
            return matchYearMatches && matchGroupMatches;
          }
        );

        console.log(`Schedule: Filtered ${filteredMatches.length} matches for year ${year}, group ${group}`);
        
        // Sort matches by match number
        const sortedMatches = filteredMatches.sort((a, b) => {
          const matchNoA = a.match_no || 0;
          const matchNoB = b.match_no || 0;
          return matchNoA - matchNoB;
        });
        
        console.log('Schedule: Matches sorted by match number');
        setMatches(sortedMatches);
      } catch (err) {
        console.error('Schedule: Error fetching data:', err);
        setError('Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [year, group]);

  console.log('Schedule: Current state:', { isLoading, error: !!error, matchesCount: matches.length });

  if (isLoading) {
    console.log('Schedule: Rendering loading state');
    return (
      <div style={{ 
        maxWidth: '100%', 
        margin: '0 auto', 
        padding: '8px',
        textAlign: 'center',
        backgroundColor: '#e3f2fd',
        minHeight: '100vh'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (error) {
    console.log('Schedule: Rendering error state:', error);
    return (
      <div style={{ 
        maxWidth: '100%', 
        margin: '0 auto', 
        padding: '8px',
        textAlign: 'center',
        color: 'red',
        backgroundColor: '#e3f2fd',
        minHeight: '100vh'
      }}>
        <div>{error}</div>
        <div style={{ marginTop: '20px', fontSize: '14px' }}>
          Debug info: year={year}, group={group}
        </div>
      </div>
    );
  }

  if (!year || !group) {
    console.log('Schedule: Rendering invalid parameters state');
    return (
      <div style={{ 
        maxWidth: '100%', 
        margin: '0 auto', 
        padding: '8px',
        textAlign: 'center',
        backgroundColor: '#e3f2fd',
        minHeight: '100vh'
      }}>
        <div>Invalid URL parameters</div>
      </div>
    );
  }

  console.log('Schedule: Rendering main content with', matches.length, 'matches');

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  return (
    <div style={{ 
      maxWidth: '100%', 
      margin: '0 auto', 
      padding: isDesktop ? '20px 40px' : '4px', 
      backgroundColor: '#e3f2fd', 
      minHeight: '100vh' 
    }}>
      {/* Navigation */}
      <div style={{ marginBottom: '20px' }}>
        <Link 
          to={`/summer-league/${year}/${group}`}
          style={{ 
            color: '#0066cc', 
            textDecoration: 'none', 
            fontSize: '16px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ← 返回
        </Link>
      </div>
      
      {/* Header */}
      <div style={{ 
        marginBottom: '32px',
        textAlign: 'center'
      }}>
        <h1 style={{ 
          fontSize: '2.5rem', 
          color: '#2d3748',
          marginBottom: '8px'
        }}>
          賽程
        </h1>
        <h2 style={{ 
          fontSize: '1.8rem', 
          color: '#4a5568',
          marginBottom: '8px'
        }}>
          Summer League {year} - Group {group}
        </h2>
      </div>

      {/* Matches Section */}
      <div>
        <h3 style={{ 
          fontSize: '1.5rem', 
          color: '#2d3748',
          marginBottom: '24px',
          borderBottom: '2px solid #e2e8f0',
          paddingBottom: '8px'
        }}>
          比賽結果
        </h3>
        
        {matches.length > 0 ? (
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
            {matches.map((match) => (
              <div key={match.id} className="schedule-match-row" style={{
                backgroundColor: '#e3f2fd',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                gap: '6px',
                flexWrap: 'wrap',
                minHeight: '80px',
                position: 'relative'
              }}>
                {/* Match Info (Left) */}
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: isDesktop ? '120px' : '60px',
                  flexShrink: 0,
                  position: isDesktop ? 'absolute' : 'static',
                  left: isDesktop ? '20px' : 'auto'
                }}>
                  <div style={{ 
                    fontSize: '1.1rem', 
                    fontWeight: 'bold',
                    color: '#2d3748',
                    marginBottom: '4px'
                  }}>
                    場次 {match.match_no}
                  </div>
                  <div style={{ 
                    fontSize: '0.9rem',
                    color: '#718096'
                  }}>
                    {formatDate(match.date)} {match.time && formatTime(match.time)}
                  </div>
                </div>

                {/* Team 1 */}
                <div className="schedule-team-col" style={{ 
                  minWidth: isDesktop ? '120px' : '60px',
                  flex: isDesktop ? '0 0 auto' : '1',
                  flexShrink: 0
                }}>
                    {match.team1 ? (
                      <Link 
                        to={`/summer-league/${year}/${group}/teams/${match.team1.id}`}
                        style={{ 
                          textDecoration: 'none', 
                          color: 'inherit',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        <div
                          className="schedule-team-logo-slot"
                          style={{ transition: 'transform 0.2s ease' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          {!logoFailed(match.team1.id) ? (
                            <img 
                              src={getTeamLogoUrl(match.team1.id)} 
                              alt={`${match.team1.name} Logo`}
                              onError={() => {
                                handleLogoError(match.team1.id);
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              backgroundColor: '#f0f0f0',
                              borderRadius: '8px',
                              border: '2px solid #ddd',
                            }} />
                          )}
                        </div>
                        <div className="schedule-team-name" style={{ 
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: '#2d3748',
                          textAlign: 'center',
                          marginBottom: '8px',
                          transition: 'color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#3182ce';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#2d3748';
                        }}
                        >
                          {match.team1.name}
                        </div>
                      </Link>
                    ) : (
                      <>
                    <div className="schedule-team-logo-slot" style={{
                      backgroundColor: '#f0f0f0',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      color: '#666',
                      border: '2px solid #ddd',
                    }}>
                          TBD
                    </div>
                  <div className="schedule-team-name" style={{ 
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#2d3748',
                    textAlign: 'center',
                    marginBottom: '8px'
                  }}>
                          TBD
                  </div>
                      </>
                    )}
                    {match.score1 !== null && match.score1 !== undefined && (
                    <div className="schedule-team-score" style={{ 
                      fontSize: '2rem', 
                      fontWeight: 'bold',
                      color: '#3182ce'
                    }}>
                      {match.score1}
                    </div>
                  )}
                </div>

                {/* VS */}
                <div className="schedule-vs" style={{ 
                  fontSize: isDesktop ? '1.2rem' : '1rem',
                  fontWeight: 'bold',
                  color: '#2d3748',
                  margin: isDesktop ? '0 20px' : '0 8px',
                  flexShrink: 0
                }}>
                  vs
                </div>

                {/* Team 2 */}
                <div className="schedule-team-col" style={{ 
                  minWidth: isDesktop ? '120px' : '60px',
                  flex: isDesktop ? '0 0 auto' : '1',
                  flexShrink: 0
                }}>
                  {match.team2 ? (
                    <Link 
                      to={`/summer-league/${year}/${group}/teams/${match.team2.id}`}
                      style={{ 
                        textDecoration: 'none', 
                        color: 'inherit',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <div
                        className="schedule-team-logo-slot"
                        style={{ transition: 'transform 0.2s ease' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        {!logoFailed(match.team2.id) ? (
                          <img 
                            src={getTeamLogoUrl(match.team2.id)} 
                            alt={`${match.team2.name} Logo`}
                            onError={() => {
                              handleLogoError(match.team2.id);
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '8px',
                            border: '2px solid #ddd',
                          }} />
                        )}
                      </div>
                      <div className="schedule-team-name" style={{ 
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: '#2d3748',
                        textAlign: 'center',
                        marginBottom: '8px',
                        transition: 'color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#3182ce';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#2d3748';
                      }}
                      >
                        {match.team2.name}
                      </div>
                    </Link>
                  ) : (
                    <>
                    <div className="schedule-team-logo-slot" style={{
                      backgroundColor: '#f0f0f0',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      color: '#666',
                      border: '2px solid #ddd',
                    }}>
                        TBD
                    </div>
                  <div className="schedule-team-name" style={{ 
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#2d3748',
                    textAlign: 'center',
                    marginBottom: '8px'
                  }}>
                        TBD
                  </div>
                    </>
                  )}
                  {match.score2 !== null && match.score2 !== undefined && (
                    <div className="schedule-team-score" style={{ 
                      fontSize: '2rem', 
                      fontWeight: 'bold',
                      color: '#3182ce'
                    }}>
                      {match.score2}
                    </div>
                  )}
                </div>

                {/* Venue and Result Link (Right) */}
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  minWidth: isDesktop ? '120px' : '60px',
                  flexShrink: 0,
                  flex: isDesktop ? '0 0 auto' : '0 0 auto',
                  position: isDesktop ? 'absolute' : 'static',
                  right: isDesktop ? '20px' : 'auto'
                }}>
                    <div style={{ 
                      fontSize: '0.75rem',
                      color: '#718096',
                      marginBottom: '8px',
                      minHeight: '20px',
                      textAlign: 'right',
                      wordBreak: 'break-word',
                      maxWidth: '100px',
                      lineHeight: '1.2'
                    }}>
                    {match.venue || ''}
                    </div>
                  <Link 
                    to={`/summer-league/matches/${match.id}`}
                    style={{
                      color: '#3182ce',
                      textDecoration: 'underline',
                      fontSize: '0.8rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    比賽結果
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            color: '#718096',
            padding: '40px'
          }}>
            No matches found for this group
          </div>
        )}
      </div>
    </div>
  );
};

export default Schedule; 